import React, { useState, useEffect, useMemo } from 'react';
import GlassSelect from './GlassSelect';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getApiKey = () => localStorage.getItem('quod_api_key') || '';

const SCORE_COLORS = {
  overall_risk_score: '#DC2626',
  opportunity_score: '#2563EB',
  change_signal_strength: '#D97706',
  intervention_likelihood: '#7C3AED',
  evidence_quality_score: '#059669',
};

const SCORE_LABELS = {
  overall_risk_score: 'Risk',
  opportunity_score: 'Opportunity',
  change_signal_strength: 'Change Strength',
  intervention_likelihood: 'Intervention',
  evidence_quality_score: 'Evidence Quality',
};

const DIM_LABELS = {
  operational_distress: 'Operational Distress',
  delivery_friction: 'Delivery Friction',
  governance_leadership: 'Governance/Leadership',
  customer_deterioration: 'Customer Deterioration',
  vendor_platform_stress: 'Vendor/Platform Stress',
  transformation_trigger: 'Transformation Trigger',
  intervention_potential: 'Intervention Potential',
  evidence_quality: 'Evidence Quality',
};

const DIM_COLORS = [
  '#DC2626', '#2563EB', '#D97706', '#7C3AED', '#059669', '#DB2777', '#0891B2', '#65A30D'
];

// ── Sparkline (inline mini chart for pipeline table) ──

export function Sparkline({ data, dataKey, color, width = 80, height = 24 }) {
  if (!data || data.length < 2) return <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>--</span>;
  return (
    <ResponsiveContainer width={width} height={height}>
      <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <Line type="monotone" dataKey={dataKey} stroke={color || '#2563EB'} strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Full Trend Chart (expandable drill-down) ──

export default function TrendChart({ companyId, companyName }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState('');
  const [showDimensions, setShowDimensions] = useState(false);
  const [visibleScores, setVisibleScores] = useState({
    overall_risk_score: true,
    opportunity_score: true,
    change_signal_strength: true,
    intervention_likelihood: true,
    evidence_quality_score: true,
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/analysis/history/${companyId}?limit=50`;
        if (scopeFilter) {
          const parts = scopeFilter.split(' > ');
          if (parts[0]) url += `&vector=${encodeURIComponent(parts[0])}`;
          if (parts[1]) url += `&sub_vector=${encodeURIComponent(parts[1])}`;
        }
        const resp = await fetch(url, { headers: { 'X-API-Key': getApiKey() } });
        if (resp.ok) {
          const data = await resp.json();
          setHistory(data.history || []);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [companyId, scopeFilter]);

  // Build scope options from data
  const scopeOptions = useMemo(() => {
    const scopes = new Set();
    history.forEach(h => {
      if (h.vector) {
        scopes.add(h.vector);
        if (h.sub_vector) scopes.add(`${h.vector} > ${h.sub_vector}`);
      }
    });
    return Array.from(scopes).sort();
  }, [history]);

  // Format chart data
  const chartData = useMemo(() => {
    return history.map(h => ({
      date: h.timestamp ? new Date(h.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '?',
      fullDate: h.timestamp ? new Date(h.timestamp).toLocaleString('en-GB') : '',
      ...h,
      ...(h.dimensions || {}),
    }));
  }, [history]);

  const toggleScore = (key) => {
    setVisibleScores(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return <div style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>Loading history...</div>;
  }

  if (chartData.length === 0) {
    return <div style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>No historical analyses found.</div>;
  }

  return (
    <div data-testid="trend-chart">
      {/* Controls */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: '600' }}>{companyName} — {chartData.length} analyses</span>

        {scopeOptions.length > 0 && (
          <GlassSelect
            value={scopeFilter}
            onChange={e => setScopeFilter(e.target.value)}
            style={{ fontSize: '11px', padding: '2px 6px' }}
            data-testid="trend-scope-filter"
          >
            <option value="">All scopes</option>
            {scopeOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </GlassSelect>
        )}

        <button
          className={`btn btn-sm ${showDimensions ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setShowDimensions(!showDimensions)}
          style={{ fontSize: '10px', padding: '2px 8px' }}
          data-testid="trend-toggle-dimensions"
        >
          {showDimensions ? 'Hide Dimensions' : 'Show Dimensions'}
        </button>
      </div>

      {/* Score toggles */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {Object.entries(SCORE_LABELS).map(([key, label]) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '11px' }}>
            <input type="checkbox" checked={visibleScores[key]} onChange={() => toggleScore(key)} />
            <span style={{ color: SCORE_COLORS[key], fontWeight: '600' }}>{label}</span>
          </label>
        ))}
      </div>

      {/* Composite scores chart */}
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--text-secondary)" />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="var(--text-secondary)" />
          <Tooltip
            contentStyle={{ fontSize: '11px', background: 'var(--surface)', border: '1px solid var(--border)' }}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.fullDate || ''}
          />
          {Object.entries(SCORE_COLORS).map(([key, color]) =>
            visibleScores[key] && (
              <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2}
                dot={{ r: 2 }} name={SCORE_LABELS[key]} connectNulls />
            )
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Dimension chart (optional) */}
      {showDimensions && chartData.some(d => Object.keys(d.dimensions || {}).length > 0) && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', color: 'var(--text-secondary)' }}>
            Weighted Dimensions Over Time
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--text-secondary)" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="var(--text-secondary)" />
              <Tooltip
                contentStyle={{ fontSize: '11px', background: 'var(--surface)', border: '1px solid var(--border)' }}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              {Object.entries(DIM_LABELS).map(([key, label], i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={DIM_COLORS[i % DIM_COLORS.length]}
                  strokeWidth={1.5} dot={false} name={label} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Signal count annotation */}
      <div style={{ marginTop: '4px', fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'right' }}>
        Latest: {chartData[chartData.length - 1]?.signal_count || 0} signals &middot;
        {chartData[chartData.length - 1]?.recommendation?.replace(/_/g, ' ') || ''}
      </div>
    </div>
  );
}
